/**
 * Run visibility and transcript reading (fix wave 2026-10-09 §4.3/§5.3, decisions 0021 and 0022). Pure:
 *
 * - `foldActivity` folds a run's stored content chunks, heartbeats and phase changes into the ephemeral `RunActivity`
 *   the Project DO relays on the project socket. It never carries tool output or reasoning content.
 * - `toolSubject` names what a tool call is about, from its input only.
 * - `scrubSecrets` blanks token-shaped strings.
 * - `foldTail` turns stored transcript chunks into the short `TailMessage`s `read_run` returns, scrubbed.
 */
import type { ActivityUsage, RunActivity, RunId } from "@minga/protocol"

// ---------------------------------------------------------------------------------------------
// Secrets
// ---------------------------------------------------------------------------------------------

export const SECRET_MARK = "«secret»"

const SECRET_PATTERNS: ReadonlyArray<RegExp> = [
  // wave K (decision 0065): kept transcripts keep what commands printed, so whole PEM private keys go (every line;
  // a block cut before its END line too), as do Stripe-style live/test keys and webhook secrets anywhere
  /-----BEGIN [A-Z ]*PRIVATE KEY-----[\s\S]*?-----END [A-Z ]*PRIVATE KEY-----/g,
  /-----BEGIN [A-Z ]*PRIVATE KEY-----[\s\S]*/g,
  /\b(?:sk|rk|pk)_(?:live|test)_[A-Za-z0-9]{10,}/g,
  /\bwhsec_[A-Za-z0-9+/=]{16,}/g,
  /eyJ[\w-]+\.[\w-]+\.[\w-]+/g, // JWT
  /sk-ant-[\w-]+/g,
  /sk-[\w-]{16,}/g,
  /gh[pousr]_\w{20,}/g,
  /github_pat_\w+/g,
  /xox[abprs]-[\w-]+/g,
  /AKIA[0-9A-Z]{16}/g,
  /Bearer\s+\S+/g,
  /\bv1\.[A-Za-z0-9_-]{16,}\.[A-Za-z0-9_-]{16,}/g, // Minga run tokens: v1.<b64url claims>.<b64url mac>
]

/** A base64 or hex run of 32+ chars (with at least one digit) within 40 chars of a secret-ish word. */
const NEAR_KEYWORD_AFTER = /(token|secret|key|password|credential)([\s\S]{0,40}?)([A-Za-z0-9+/=_-]{32,})/gi
/**
 * The run BEFORE the word, matched on the reversed text (wave K): the forward pattern starts at every character of a
 * long run and backtracks, which is quadratic (a 5 KB base64 blob took 1.6 s); anchored on the reversed word it is linear.
 */
const NEAR_KEYWORD_BEFORE_REVERSED =
  /(nekot|terces|yek|drowssap|laitnederc)([\s\S]{0,40}?)([A-Za-z0-9+/=_-]{32,})/gi
const KEYWORD = /token|secret|key|password|credential/i
const reverse = (s: string): string => [...s].reverse().join("")
const SECRET_MARK_REVERSED = reverse("«secret»")
const looksRandom = (run: string): boolean => /\d/.test(run) && run !== SECRET_MARK

/** Wave K: `scheme://user:password@host` → `scheme://«secret»@host` (the scheme is bounded: no backtracking blowup). */
const URL_USERINFO = /\b([a-z][a-z0-9+.-]{0,31}:\/\/)[^\s/:@]+:[^\s/@]+@/gi
/** Wave K: `.env`-style lines (`KEY=value`, `export KEY=value`): the value goes unless it is short or a plain word or number. */
const ENV_LINE = /^(\s*(?:export\s+)?[A-Z][A-Z0-9_]{2,}\s*=\s*)(\S.*)$/gm
const PLAIN_VALUE = /^(?:[A-Za-z][A-Za-z_-]*|-?\d+(?:\.\d+)?)$/

/** Replace secret-looking substrings with `«secret»`. Idempotent. */
export const scrubSecrets = (text: string): string => {
  let out = text
  for (const re of SECRET_PATTERNS) out = out.replace(re, SECRET_MARK)
  out = out.replace(URL_USERINFO, (_all, scheme: string) => `${scheme}${SECRET_MARK}@`)
  out = out.replace(ENV_LINE, (all, head: string, value: string) => {
    const v = value.trim()
    return v.length < 8 || PLAIN_VALUE.test(v) ? all : `${head}${SECRET_MARK}`
  })
  if (!KEYWORD.test(out)) return out
  out = out.replace(NEAR_KEYWORD_AFTER, (all, word: string, gap: string, run: string) =>
    looksRandom(run) ? `${word}${gap}${SECRET_MARK}` : all,
  )
  out = reverse(
    reverse(out).replace(NEAR_KEYWORD_BEFORE_REVERSED, (all, word: string, gap: string, run: string) =>
      looksRandom(run) ? `${word}${gap}${SECRET_MARK_REVERSED}` : all,
    ),
  )
  return out
}

// ---------------------------------------------------------------------------------------------
// Tool subjects
// ---------------------------------------------------------------------------------------------

const SHELL_TOOLS = new Set(["bash", "shell", "exec", "commandexecution", "local_shell", "exec_command"])
const FILE_TOOLS = new Set(["read", "write", "edit", "multiedit", "notebookedit", "notebookread", "ls"])
const SEARCH_TOOLS = new Set(["grep", "glob", "search", "find"])
const WEB_URL_TOOLS = new Set(["webfetch", "web_fetch", "fetch"])
const WEB_QUERY_TOOLS = new Set(["websearch", "web_search"])

const str = (v: unknown): string | null => (typeof v === "string" && v.trim().length > 0 ? v : null)
const field = (input: unknown, ...keys: ReadonlyArray<string>): unknown => {
  if (typeof input !== "object" || input === null) return undefined
  const o = input as Record<string, unknown>
  for (const k of keys) if (o[k] !== undefined) return o[k]
  return undefined
}
const oneLine = (s: string, max: number): string => {
  const t = s.replace(/\s+/g, " ").trim()
  return t.length > max ? `${t.slice(0, max - 1)}…` : t
}

/** The host of a URL (no credentials, path or query); null when it is not an absolute URL. */
const hostOf = (url: string): string | null => {
  const m = /^[a-z][a-z0-9+.-]*:\/\/([^/?#\s]+)/i.exec(url.trim())
  if (!m?.[1]) return null
  const authority = m[1]
  const host = authority.slice(authority.lastIndexOf("@") + 1)
  return host.length > 0 ? host.toLowerCase() : null
}

/**
 * What a tool call is about, from its **input only** (never its output): shell → the first 100 chars of the command;
 * file tools → the path; search → the pattern; web → the URL host (search: the query); `mcp__<server>__<tool>` →
 * `server.tool`; anything else → null.
 */
/** Codex runs commands as `/bin/zsh -lc '<cmd>'`: the subject is the command, not the shell around it. */
const SHELL = /^(?:\S*\/)?(?:ba|z|da)?sh$/
const SHELL_WRAPPER = /^(\S+)\s+-l?c\s+(?:'([^']*)'|"([^"]*)")\s*$/
const unwrapShell = (cmd: string): string => {
  const m = SHELL_WRAPPER.exec(cmd.trim())
  return m?.[1] && SHELL.test(m[1]) ? (m[2] ?? m[3] ?? cmd) : cmd
}

export const toolSubject = (name: string, input: unknown): string | null => {
  if (name.startsWith("mcp__")) {
    const rest = name.slice(5)
    const i = rest.indexOf("__")
    return i > 0 ? `${rest.slice(0, i)}.${rest.slice(i + 2)}` : rest || null
  }
  const n = name.toLowerCase()
  if (SHELL_TOOLS.has(n)) {
    const cmd = field(input, "command", "cmd")
    const argv = Array.isArray(cmd) ? cmd.filter((x): x is string => typeof x === "string") : null
    const text =
      argv && argv.length === 3 && SHELL.test(argv[0] ?? "") && /^-l?c$/.test(argv[1] ?? "")
        ? (argv[2] ?? null)
        : argv
          ? argv.join(" ")
          : str(cmd)
    return text ? oneLine(argv ? text : unwrapShell(text), 100) : null
  }
  if (FILE_TOOLS.has(n)) {
    const p = str(field(input, "file_path", "path", "notebook_path"))
    return p ? oneLine(p, 200) : null
  }
  if (n === "apply_patch") {
    const changes = field(input, "changes")
    const first = Array.isArray(changes) ? str(field(changes[0], "path")) : null
    if (!first) return null
    const more = Array.isArray(changes) && changes.length > 1 ? ` +${changes.length - 1}` : ""
    return `${oneLine(first, 200)}${more}`
  }
  if (SEARCH_TOOLS.has(n)) {
    const p = str(field(input, "pattern", "query"))
    return p ? oneLine(p, 100) : null
  }
  if (WEB_URL_TOOLS.has(n)) {
    const u = str(field(input, "url"))
    return u ? hostOf(u) : null
  }
  if (WEB_QUERY_TOOLS.has(n)) {
    const q = str(field(input, "query"))
    return q ? oneLine(q, 100) : null
  }
  return null
}

// ---------------------------------------------------------------------------------------------
// RunActivity
// ---------------------------------------------------------------------------------------------

/** A stored transcript chunk (UIMessageChunk or a body-made `data-run-*` view chunk). */
export interface ActivityChunk {
  readonly type: string
  readonly [k: string]: unknown
}

export type ActivityPhase = RunActivity["phase"]

export type ActivityStep =
  /** A stored content frame, as the viewer sees it (`viewChunk`). */
  | { readonly kind: "chunk"; readonly at: number; readonly chunk: ActivityChunk }
  /** A heartbeat (never stored). */
  | {
      readonly kind: "heartbeat"
      readonly at: number
      readonly phase: ActivityPhase
      readonly idleMs: number
      readonly model?: string | undefined
      readonly usageSoFar?: ActivityUsage | undefined
    }
  /** turn.accepted of a new epoch: counts restart. */
  | { readonly kind: "accepted"; readonly at: number }
  /** The sink's own view of the phase changed (ended, waiting, …). */
  | { readonly kind: "phase"; readonly at: number; readonly phase: ActivityPhase }

export const EXCERPT_MAX = 140
export const ERROR_MAX = 200

type Last = RunActivity["last"]

const empty = (runId: RunId, epoch: number, at: number): RunActivity => ({
  runId,
  epoch: epoch as RunActivity["epoch"],
  at: at as RunActivity["at"],
  phase: "waiting",
  idleMs: 0 as RunActivity["idleMs"],
  model: null,
  frames: 0 as RunActivity["frames"],
  toolCalls: 0 as RunActivity["toolCalls"],
  last: null,
  usage: null,
})

const clip = (s: string, max: number): string => (s.length > max ? `${s.slice(0, max - 1)}…` : s)
const errorLast = (message: unknown): Last => ({
  _tag: "error",
  message: clip(scrubSecrets(oneLine(typeof message === "string" ? message : "error", 10_000)), ERROR_MAX),
})
const data = (c: ActivityChunk): Record<string, unknown> =>
  typeof c.data === "object" && c.data !== null ? (c.data as Record<string, unknown>) : {}

const withToolState = (last: Last, state: "done" | "error" | "denied"): Last =>
  last?._tag === "tool" ? { ...last, state } : last

/** One chunk's effect on `last` and the phase. */
const chunkStep = (a: RunActivity, c: ActivityChunk): RunActivity => {
  const running: ActivityPhase =
    a.phase === "waiting" || a.phase === "preparing" || a.phase === "awaiting_permission"
      ? "running"
      : a.phase
  switch (c.type) {
    case "data-run-accepted":
      return { ...a, phase: "running" }
    case "tool-input-start":
      return {
        ...a,
        phase: running,
        last: { _tag: "tool", name: String(c.toolName ?? "tool"), subject: null, state: "running" },
      }
    case "tool-input-available": {
      const name = String(c.toolName ?? "tool")
      const subject = toolSubject(name, c.input)
      return {
        ...a,
        phase: running,
        toolCalls: (a.toolCalls + 1) as RunActivity["toolCalls"],
        last: {
          _tag: "tool",
          name,
          subject: subject === null ? null : scrubSecrets(subject),
          state: "running",
        },
      }
    }
    case "tool-output-available":
      return { ...a, phase: running, last: withToolState(a.last, "done") }
    case "tool-output-error":
    case "tool-input-error":
      return { ...a, phase: running, last: withToolState(a.last, "error") }
    case "tool-output-denied":
      return { ...a, phase: running, last: withToolState(a.last, "denied") }
    case "text-start":
      return { ...a, phase: running, last: { _tag: "text", excerpt: "" } }
    case "text-delta": {
      const prev = a.last?._tag === "text" ? a.last.excerpt : ""
      if (prev.length >= EXCERPT_MAX) return { ...a, phase: running }
      const delta = typeof c.delta === "string" ? c.delta : ""
      const joined = `${prev}${delta}`.replace(/\s+/g, " ").trimStart()
      return {
        ...a,
        phase: running,
        last: { _tag: "text", excerpt: clip(scrubSecrets(joined), EXCERPT_MAX) },
      }
    }
    case "reasoning-start":
    case "reasoning-delta":
      return { ...a, phase: running, last: { _tag: "thinking" } }
    case "data-run-permission": {
      const d = data(c)
      return {
        ...a,
        phase: "awaiting_permission",
        last: {
          _tag: "permission",
          tool: String(d.tool ?? "tool"),
          title: clip(scrubSecrets(String(d.title ?? "")), EXCERPT_MAX),
        },
      }
    }
    case "data-cli-error":
      return { ...a, last: errorLast(data(c).message) }
    case "data-run-failed":
      return { ...a, phase: "finishing", last: errorLast(data(c).error ?? data(c).stopReason) }
    case "data-run-completed":
      return { ...a, phase: "finishing" }
    default:
      return a.phase === "waiting" || a.phase === "preparing" ? { ...a, phase: running } : a
  }
}

/**
 * Fold one step into a run's activity. `prev` of another (older) epoch, or an `accepted` step, restarts the counts.
 * An ended activity stays ended for chunks and heartbeats (only a phase step changes it).
 */
export const foldActivity = (
  prev: RunActivity | null,
  step: ActivityStep,
  ids: { readonly runId: RunId; readonly epoch: number },
): RunActivity => {
  let a =
    prev && prev.runId === ids.runId && prev.epoch >= ids.epoch ? prev : empty(ids.runId, ids.epoch, step.at)
  switch (step.kind) {
    case "accepted":
      return { ...empty(ids.runId, ids.epoch, step.at), model: a.model, phase: "running" }
    case "phase":
      return { ...a, phase: step.phase }
    case "heartbeat":
      return {
        ...a,
        phase: a.phase === "ended" ? a.phase : step.phase,
        idleMs: Math.max(0, Math.floor(step.idleMs)) as RunActivity["idleMs"],
        model: step.model ?? a.model,
        usage: step.usageSoFar ?? a.usage,
      }
    case "chunk": {
      a = {
        ...a,
        at: Math.max(a.at, step.at) as RunActivity["at"],
        frames: (a.frames + 1) as RunActivity["frames"],
      }
      const next = chunkStep(a, step.chunk)
      return a.phase === "ended" ? { ...next, phase: "ended" } : next
    }
  }
}

/**
 * The push-worthy identity of an activity: a change here is pushed at once, anything else is throttled. A tool call
 * whose subject just arrived counts (its `tool-input-start` has none yet): a quick `Bash · echo hi` would otherwise
 * only ever show as "Bash".
 */
export const activityKey = (a: RunActivity | null): string =>
  a === null
    ? ""
    : `${a.epoch}|${a.phase}|${a.last?._tag ?? "none"}|${a.last?._tag === "permission" ? `${a.last.tool}:${a.last.title}` : ""}|${a.last?._tag === "tool" && a.last.subject !== null ? "subject" : ""}`

// ---------------------------------------------------------------------------------------------
// Transcript tail (read_run)
// ---------------------------------------------------------------------------------------------

export type TailMessage =
  | { readonly kind: "text"; readonly frameSeq: number; readonly text: string }
  | {
      readonly kind: "tool"
      readonly frameSeq: number
      readonly name: string
      readonly input: string
      readonly isError: boolean
      readonly output: string | null
    }
  | {
      readonly kind: "permission" | "refusal" | "error" | "milestone"
      readonly frameSeq: number
      readonly text: string
    }

export const TAIL_TEXT_MAX = 2_000
export const TAIL_INPUT_MAX = 200
export const TAIL_OUTPUT_MAX = 300

const json = (v: unknown): string => {
  if (typeof v === "string") return v
  try {
    return JSON.stringify(v) ?? ""
  } catch {
    return String(v)
  }
}
const scrubClip = (s: string, max: number): string => clip(scrubSecrets(s), max)

/** A stored frame of the transcript, as the viewer sees it. */
export interface TailFrame {
  readonly frameSeq: number
  readonly chunk: ActivityChunk
}

/**
 * The transcript as short messages, oldest first, every string scrubbed: assistant text (≤ 2,000 chars per part),
 * tool calls (input ≤ 200, output ≤ 300), permissions, policy refusals, CLI errors and run milestones. Reasoning is
 * never included. Each message carries the frameSeq of its first chunk (the paging key).
 */
export const foldTail = (frames: ReadonlyArray<TailFrame>): Array<TailMessage> => {
  const out: Array<TailMessage | null> = []
  const texts = new Map<string, { index: number; frameSeq: number; text: string }>()
  const tools = new Map<string, number>()
  const flushText = (id: string) => {
    const t = texts.get(id)
    if (!t) return
    texts.delete(id)
    const text = t.text.trim()
    out[t.index] = text ? { kind: "text", frameSeq: t.frameSeq, text: scrubClip(text, TAIL_TEXT_MAX) } : null
  }
  const toolUpdate = (id: string, isError: boolean, output: string) => {
    const i = tools.get(id)
    const m = i === undefined ? undefined : out[i]
    if (i === undefined || m?.kind !== "tool") return
    out[i] = { ...m, isError, output: scrubClip(output, TAIL_OUTPUT_MAX) }
  }
  for (const { frameSeq, chunk: c } of frames) {
    const id = typeof c.id === "string" ? c.id : ""
    const d = data(c)
    switch (c.type) {
      case "text-start":
        flushText(id)
        texts.set(id, { index: out.push(null) - 1, frameSeq, text: "" })
        break
      case "text-delta": {
        let t = texts.get(id)
        if (!t) {
          t = { index: out.push(null) - 1, frameSeq, text: "" }
          texts.set(id, t)
        }
        // keep a little more than the cap so the final clip marks the cut
        if (t.text.length <= TAIL_TEXT_MAX) t.text += typeof c.delta === "string" ? c.delta : ""
        break
      }
      case "text-end":
        flushText(id)
        break
      case "tool-input-available": {
        const callId = String(c.toolCallId ?? "")
        const msg: TailMessage = {
          kind: "tool",
          frameSeq,
          name: String(c.toolName ?? "tool"),
          input: scrubClip(json(c.input), TAIL_INPUT_MAX),
          isError: false,
          output: null,
        }
        const existing = tools.get(callId)
        if (existing !== undefined) out[existing] = msg
        else tools.set(callId, out.push(msg) - 1)
        break
      }
      case "tool-output-available":
        toolUpdate(String(c.toolCallId ?? ""), false, json(c.output))
        break
      case "tool-output-error":
      case "tool-input-error":
        toolUpdate(String(c.toolCallId ?? ""), true, json(c.errorText ?? "error"))
        break
      case "tool-output-denied":
        toolUpdate(String(c.toolCallId ?? ""), true, "denied")
        break
      case "data-run-permission":
        out.push({
          kind: "permission",
          frameSeq,
          text: scrubClip(`${String(d.tool ?? "tool")}: ${String(d.title ?? "")}`, TAIL_TEXT_MAX),
        })
        break
      case "data-policy-refusal":
        out.push({
          kind: "refusal",
          frameSeq,
          text: scrubClip(String(d.message ?? d.reason ?? d.text ?? json(d)), TAIL_TEXT_MAX),
        })
        break
      case "data-cli-error":
        out.push({ kind: "error", frameSeq, text: scrubClip(String(d.message ?? "error"), TAIL_TEXT_MAX) })
        break
      case "data-run-failed":
        out.push({
          kind: "error",
          frameSeq,
          text: scrubClip(`failed (${String(d.stopReason ?? "?")}): ${String(d.error ?? "")}`, TAIL_TEXT_MAX),
        })
        break
      case "data-run-completed":
        out.push({ kind: "milestone", frameSeq, text: `completed (${String(d.stopReason ?? "?")})` })
        break
      case "data-run-accepted": {
        const access =
          typeof d.access === "object" && d.access !== null
            ? (d.access as { text?: unknown; mcpServers?: unknown })
            : null
        // G15: the MCP servers the run's CLI actually loaded (account connectors included)
        const mcp =
          Array.isArray(access?.mcpServers) &&
          access.mcpServers.length > 0 &&
          access.mcpServers.every((x) => typeof x === "string")
            ? ` · MCP: ${(access.mcpServers as ReadonlyArray<string>).join(", ")}`
            : ""
        out.push({
          kind: "milestone",
          frameSeq,
          text: scrubClip(
            `started on ${String(d.profileId ?? "?")} (${String(d.resumed ?? "fresh")})${access && typeof access.text === "string" ? ` · ${access.text}` : ""}${mcp}`,
            TAIL_TEXT_MAX,
          ),
        })
        break
      }
      case "data-cli-notice":
        out.push({ kind: "milestone", frameSeq, text: scrubClip(String(d.text ?? ""), TAIL_TEXT_MAX) })
        break
      default:
        break
    }
  }
  for (const id of [...texts.keys()]) flushText(id)
  return out.filter((m): m is TailMessage => m !== null)
}

/** One page of the tail: the last `limit` messages before `before` (frameSeq), and the cursor of the next page. */
export const tailPage = (
  messages: ReadonlyArray<TailMessage>,
  limit: number,
  before: number | null,
): { readonly messages: ReadonlyArray<TailMessage>; readonly nextBefore: number | null } => {
  const eligible = before === null ? messages : messages.filter((m) => m.frameSeq < before)
  const page = eligible.slice(Math.max(0, eligible.length - limit))
  const first = page[0]
  return { messages: page, nextBefore: first && eligible.length > page.length ? first.frameSeq : null }
}
